/* eslint-disable */
import React, { startTransition, useMemo } from 'react';
import { supabase } from '../../../supabase';
import { PORTAL_NAV } from '../../../constants/portals';
import { useShellStore } from '../shellStore';
import { useSavedViews, serializeSets, reviveSets } from '../useSavedViews';
import FilterBar from '../FilterBar';

// ============================================================
// OriginalsFilterBar — the Originals › Items bar in the v3 grammar (S3). Every
// control drives the SAME state CatalogActionBar drives: the useCatalogFilters
// Sets (fBrand · fGender · fType · fMat · fShape · fAssigned · fCreatedBy —
// fStatus stays hidden, David's Q2), `search` (server-side, 220 ms in the
// hook, #57), `sortBy` (+ localStorage clb_sort + user_profiles.preferences,
// as SortButton), Export / Import / New item. The 3-state reverse-search note
// (linked dev) rides the status slot.
// ============================================================

const SORT_ITEMS = [
  { key: 'newest', label: 'Newest first' }, { key: 'oldest', label: 'Oldest first' }, { key: 'brand_az', label: 'Brand A → Z' }, { key: 'brand_za', label: 'Brand Z → A' },
  { key: 'shape_az', label: 'Shape A → Z' }, { key: 'updated', label: 'Recently updated' }, { key: 'sku_az', label: 'SKU A → Z' },
];
const EMPTY = new Set();
const FILTER_BY = [{ key: '__search', label: 'SKU', icon: 'hash' }, { key: 'fBrand', label: 'Brand', icon: 'bookmark' }, { key: 'fShape', label: 'Shape', icon: 'glasses' }, { key: '__search', label: 'Dev no. (linked)', icon: 'back' }];
const opt = (list) => (list || []).filter((o) => o && o !== 'All').map((o) => ({ value: o, label: o }));

export default function OriginalsFilterBar({ user, perm, tab, setTab, catalog, condensed, chromeProps, showToast }) {
  const c = catalog || {};
  const density = useShellStore((s) => s.density);
  const setDensity = useShellStore((s) => s.setDensity);
  const sets = { fBrand: c.fBrand, fGender: c.fGender, fType: c.fType, fMat: c.fMat, fShape: c.fShape, fAssigned: c.fAssigned, fCreatedBy: c.fCreatedBy };
  const setters = { fBrand: c.setFBrand, fGender: c.setFGender, fType: c.setFType, fMat: c.setFMat, fShape: c.setFShape, fAssigned: c.setFAssigned, fCreatedBy: c.setFCreatedBy };
  const set = (k) => (next) => startTransition(() => setters[k] && setters[k](next));

  const sections = useMemo(() => {
    const p = PORTAL_NAV.find((x) => x.key === 'catalog');
    const total = Math.max(c.unfilteredTotal || 0, c.totalCount || 0);
    return (p?.subsections || []).map((s) => ({ key: s.tab, label: s.label, count: s.tab === 'catalog' && !c.isCurrentlyFiltered && total ? total : undefined }));
  }, [c.isCurrentlyFiltered, c.unfilteredTotal, c.totalCount]);

  const saved = useSavedViews('originals', user?.email);
  const views = {
    list: saved.views.map((v) => ({ id: v.id, name: v.name })),
    onApply: (v) => { const st = saved.views.find((x) => x.id === v.id)?.state; if (!st) return; startTransition(() => { const f = reviveSets(st.filters || {}); Object.keys(setters).forEach((k) => setters[k] && setters[k](f[k] || new Set())); c.setSearch && c.setSearch(st.search || ''); if (st.sort && c.setSortBy) c.setSortBy(st.sort); }); },
    onSave: (name) => { saved.save(name, { filters: serializeSets(sets), search: c.search || '', sort: c.sortBy }); showToast && showToast(`View “${name}” saved`); },
    onDelete: (v) => saved.remove(v.id),
    iconOnly: true,
  };

  const fType = sets.fType || EMPTY;
  const typeValue = fType.size === 1 ? Array.from(fType)[0] : 'all';
  const fields = [
    { key: 'fType', type: 'seg', alt: 'Line', value: typeValue, defaultValue: 'all', options: [{ value: 'all', label: 'All' }, ...opt(c.typeOpts).map((o) => ({ ...o, label: /sun/i.test(o.value) ? 'Sun' : o.value }))], onChange: (v) => set('fType')(v === 'all' ? new Set() : new Set([v])), priority: 5 },
    { key: 'fBrand', label: 'Brand', options: opt(c.allBrands), value: sets.fBrand || EMPTY, onChange: set('fBrand'), priority: 1 },
    { key: 'fShape', label: 'Shape', options: opt(c.shapeOpts), value: sets.fShape || EMPTY, onChange: set('fShape'), priority: 2 },
    { key: 'fGender', label: 'Gender', options: opt(c.genderOpts), value: sets.fGender || EMPTY, onChange: set('fGender'), searchable: false, priority: 3 },
    { key: 'fMat', label: 'Material', options: opt(c.matOpts), value: sets.fMat || EMPTY, onChange: set('fMat'), priority: 4 },
    { key: 'fAssigned', label: 'Assigned to', options: opt(c.assignedOpts), value: sets.fAssigned || EMPTY, onChange: set('fAssigned'), priority: 5 },
    { key: 'fCreatedBy', label: 'Created by', options: opt(c.createdByOpts), value: sets.fCreatedBy || EMPTY, onChange: set('fCreatedBy'), priority: 5 },
  ];

  const onSort = (k) => {
    c.setSortBy && c.setSortBy(k);
    try { localStorage.setItem('clb_sort', k); } catch (e) { /* session only */ }
    if (user?.id) {
      supabase.from('user_profiles').select('preferences').eq('id', user.id).single()
        .then(({ data }) => { const prefs = data?.preferences || {}; supabase.from('user_profiles').update({ preferences: { ...prefs, sort_by: k } }).eq('id', user.id); })
        .catch(() => {});
    }
  };
  const status = c.linkedDevMode && c.linkedDevMode !== 'none'
    ? [{ dot: c.linkedDevMode === 'matched' ? 'amber' : 'grey', text: c.linkedDevMode === 'matched' ? `linked from ${c.linkedDevQuery}` : `no items linked from ${c.linkedDevQuery} · text matches`, title: c.linkedDevMode === 'matched' ? `Showing the originals linked from development ${c.linkedDevQuery}` : `No original is linked from ${c.linkedDevQuery} — showing free-text matches` }]
    : [{ dot: 'live', text: 'Live', title: 'The grid follows the database live' }];

  const cfg = {
    portal: { key: 'catalog', name: 'Originals', icon: 'glasses' },
    sections, section: tab, onSection: (k) => setTab(k),
    count: c.unfilteredTotal != null || c.totalCount != null ? { total: Math.max(c.unfilteredTotal || 0, c.totalCount || 0), shown: c.totalCount || 0, filtered: !!c.isCurrentlyFiltered } : null,
    status,
    fields, onClearAll: () => startTransition(() => c.clearAllFilters && c.clearAllFilters()),
    search: { scope: 'originals', value: c.search || '', onChange: (v) => c.setSearch && c.setSearch(v), placeholder: 'Brand, SKU, shape, dev no.…', filterBy: FILTER_BY },
    sort: { value: c.sortBy, items: SORT_ITEMS, onPick: onSort },
    views,
    density: { value: density, onChange: setDensity },
    actions: [
      perm?.canExport ? { key: 'export', icon: 'download', label: 'Export', title: 'Export Excel', onClick: () => c.setShowExport && c.setShowExport(true) } : null,
      perm?.canExport ? { key: 'import', icon: 'upload', label: 'Import', title: 'Import Excel', onClick: () => c.setShowImport && c.setShowImport(true) } : null,
    ].filter(Boolean),
    primary: perm?.canAdd ? { label: 'New item', onClick: () => c.setShowAdd && c.setShowAdd(true) } : null,
  };
  return <FilterBar cfg={cfg} condensed={condensed} {...chromeProps} />;
}
